/**
 * True while *entry* is a direct file upload whose server-side processing has
 * not reported an end; with *uploadStatusId*, only for that upload.
 */
export function isProcessingUpload(entry, uploadStatusId = entry?.uploadStatusId) {
    return Boolean(
        entry?.uploadStatusId &&
            entry.uploadStatusId === uploadStatusId &&
            entry.phase === 'processing' &&
            !entry.isCompleted &&
            !entry.isError
    );
}

/**
 * True when the end of processing moves *entry* on to its label or trajectory
 * generation step at once, as `handleWsMessage` does; a deferred one waits for
 * the task's model setup to arm it.
 */
export function startsGenerationAfterProcessing(entry) {
    return Boolean(entry?.isLabelGeneration && (!entry.deferLabelGeneration || entry.labelGenerationArmed));
}

/**
 * Direct file uploads whose server-side processing has not reported an end,
 * as [progress key, entry] pairs. Multi-objective groups are left out: their
 * status row completes at the fan-out, before the per-task clones finish.
 */
export function processingUploadsWithStatus(progressStatus, groupKeys) {
    return Object.entries(progressStatus || {}).filter(
        ([key, entry]) => isProcessingUpload(entry) && !groupKeys?.has(key)
    );
}

/**
 * The latest `updateProgress` message the WebSocket would have delivered for
 * an upload, built from its `GET upload-status/{status_id}` record: its end
 * state, or which step it is on and how far, so a reconnect restores the
 * analysis / import rows. Null before the server has reported any progress.
 */
export function uploadProgressEventFromStatus(record, key, entry) {
    const event = { action: 'updateProgress', datasetName: key, usecaseId: entry?.usecaseId };
    if (record?.status === 'completed') {
        return { ...event, progress: 100, isCompleted: true, isError: false };
    }
    if (record?.status === 'failed') {
        return {
            ...event,
            progress: 0,
            isCompleted: false,
            isError: true,
            message: 'Error processing file',
            details: record.error,
        };
    }
    if (record?.status !== 'processing') return null;
    if (record.total_rows > 0) {
        const processed = Math.min(record.processed_rows || 0, record.total_rows);
        return {
            ...event,
            // Only the terminal event completes the card; finalization runs after the last row.
            progress: Math.min(99.9, (processed / record.total_rows) * 100),
            ingestPhase: 'importing_rows',
            processedRows: processed,
            totalRows: record.total_rows,
        };
    }
    if (Number.isFinite(record.analyzed_rows)) {
        return {
            ...event,
            progress: 0,
            message: 'Analyzing file',
            ingestPhase: 'analyzing_file',
            processedRows: record.analyzed_rows,
            totalRows: record.analysis_total_rows ?? null,
        };
    }
    return null;
}
